"use client";

import NextLink from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/auth-context";
import { useTheme } from "@/app/providers";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

type Currency =
  | "USD"
  | "LKR"
  | "INR"
  | "SGD"
  | "MYR"
  | "THB"
  | "CNY"
  | "JPY"
  | "AED"
  | "EUR"
  | "GBP"
  | "AUD"
  | "CAD";

function CurrencyDropdown() {
  const [currency, setCurrency] = useState<Currency>("USD");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("currency");
      if (saved) setCurrency(saved as Currency);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("currency", currency);
    } catch {}
  }, [currency]);

  return (
    <select
      className="rounded-xl border border-purple-600 hover:border-purple-700 focus:border-purple-600 focus:ring-2 focus:ring-purple-500 focus:outline-none px-3 py-2 bg-white text-sm w-full md:w-auto text-gray-800 transition-colors duration-200"
      value={currency}
      onChange={(e) => setCurrency(e.target.value as Currency)}
      aria-label="Currency"
    >
      <option value="USD">USD</option>
      <option value="LKR">LKR</option>
      <option value="INR">INR</option>
      <option value="SGD">SGD</option>
      <option value="MYR">MYR</option>
      <option value="THB">THB</option>
      <option value="CNY">CNY</option>
      <option value="JPY">JPY</option>
      <option value="AED">AED</option>
      <option value="EUR">EUR</option>
      <option value="GBP">GBP</option>
      <option value="AUD">AUD</option>
      <option value="CAD">CAD</option>
    </select>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  return (
    <button
      onClick={toggleTheme}
      className={`flex items-center justify-center w-9 h-9 p-2 bg-transparent transition-colors ${
        isHomePage
          ? "text-white hover:opacity-80"
          : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400"
      }`}
      aria-label="Toggle dark mode"
    >
      {theme === "light" ? (
        // Moon icon
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      ) : (
        // Sun icon
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="text-yellow-500"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      )}
    </button>
  );
}

// ── Sign In Dropdown ──────────────────────────────────────────────────────────
const signInDropdownStyle = `
  @keyframes signInSlideDown {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @keyframes signInItemIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .signin-dropdown {
    animation: signInSlideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  .signin-item-1 { animation: signInItemIn 0.25s 0.05s cubic-bezier(0.16,1,0.3,1) both; }
  .signin-item-2 { animation: signInItemIn 0.25s 0.12s cubic-bezier(0.16,1,0.3,1) both; }
`;

function SignInDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setIsOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  return (
    <>
      <style>{signInDropdownStyle}</style>
      <div className="relative" ref={ref}>
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          aria-expanded={isOpen}
          aria-label="Sign in options"
          className={`group relative flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 border-2 overflow-hidden
            ${
              isOpen
                ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-200 dark:shadow-purple-900/40"
                : isHomePage
                  ? "border-white/60 text-white hover:bg-white hover:text-purple-700 hover:shadow-md"
                  : "border-purple-600 text-purple-700 dark:text-purple-400 hover:bg-purple-600 hover:text-white hover:shadow-md hover:shadow-purple-200 dark:hover:shadow-purple-900/30"
            }`}
        >
          {/* Shimmer effect on hover */}
          <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <svg
            className="w-4 h-4 relative z-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
            />
          </svg>
          <span className="relative z-10">Sign In</span>
          <svg
            className={`w-3.5 h-3.5 relative z-10 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Dropdown panel */}
        {isOpen && (
          <div className="signin-dropdown absolute right-0 top-[calc(100%+10px)] w-72 z-50">
            {/* Arrow */}
            <div className="absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-900 border-l border-t border-gray-200 dark:border-gray-700 rotate-45 shadow-[-2px_-2px_4px_0px_rgba(0,0,0,0.04)]" />

            <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl shadow-gray-200/60 dark:shadow-black/30 overflow-hidden">
              {/* Top accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-violet-500 to-amber-400" />

              <div className="p-2">
                {/* Customer option */}
                <NextLink
                  href="/customer/sign-in"
                  onClick={() => setIsOpen(false)}
                  className="signin-item-1 group flex items-center gap-3.5 p-3.5 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-150"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center group-hover:scale-110 group-hover:bg-purple-600 transition-all duration-200">
                    <svg
                      className="w-5 h-5 text-purple-600 group-hover:text-white transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors">
                      Customer
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Book hotels, tours & experiences
                    </p>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-all"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </NextLink>

                <div className="my-1.5 border-t border-dashed border-gray-100 dark:border-gray-800 mx-3" />

                {/* Partner option */}
                <NextLink
                  href="/partner/sign-in-property"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="signin-item-2 group flex items-center gap-3.5 p-3.5 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all duration-150"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 group-hover:bg-amber-500 transition-all duration-200">
                    <svg
                      className="w-5 h-5 text-amber-600 group-hover:text-white transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">
                      Partner
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Manage your property & listings
                    </p>
                  </div>
                  <svg
                    className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </NextLink>
              </div>

              {/* Footer hint */}
              <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                  New here?{" "}
                  <NextLink
                    href="/customer/sign-up"
                    onClick={() => setIsOpen(false)}
                    className="text-purple-600 dark:text-purple-400 font-medium hover:underline"
                  >
                    Create a free account
                  </NextLink>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Profile Dropdown Component - MODAL STYLE WITH BLUR
function ProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, logout } = useAuth(); // ✅ Get auth context

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // ✅ Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const handleSignOut = () => {
    setIsOpen(false);
    logout(); // ✅ Call logout from auth context
  };

  return (
    <>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Profile menu"
        aria-expanded={isOpen}
      >
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-gray-200">
          <Image
            src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/logo/profile.jpg`}
            alt="User profile"
            width={60}
            height={60}
            className="w-full h-full object-cover"
            unoptimized
          />
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* Modal Overlay with Blur */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-30 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
        >
          {/* Profile Header with Avatar */}

          {/* Close Button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            aria-label="Close menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-4 border-white shadow-lg mb-3">
              <Image
                src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/logo/profile.jpg`}
                alt="User profile"
                width={80}
                height={80}
                className="w-full h-full object-cover"
                unoptimized
              />
            </div>
            <h3 className="font-bold text-white text-lg">Kevin Silva</h3>
            <p className="text-purple-100 text-sm">kevin@email.com</p> */}

          {/* Menu Items */}
          <nav className="py-2">
            <NextLink
              href="/customer/profile/account"
              className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors border-b border-gray-100 dark:border-gray-700"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>My account</span>
            </NextLink>

            <NextLink
              href="/customer/profile/bookings"
              className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors border-b border-gray-100 dark:border-gray-700"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>Bookings & Trips</span>
            </NextLink>

            {/* <NextLink
              href="/customer/profile/reviews"
className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors border-b border-gray-100 dark:border-gray-700"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
              <span>Reviews</span>
            </NextLink> */}

            <NextLink
              href="/customer/profile/wishlist"
              className="flex items-center gap-3 px-6 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors border-b border-gray-100 dark:border-gray-700"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span>Saved</span>
            </NextLink>

            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-6 py-3 text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>Sign out</span>
            </button>
          </nav>
        </div>
      )}
    </>
  );
}

// Desktop Hamburger Dropdown Component
function DesktopHamburgerDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  return (
    <div className="relative hidden lg:block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center p-2 transition-colors bg-transparent ${
          isHomePage
            ? "text-white hover:opacity-80"
            : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400"
        }`}
        aria-label="Additional menu"
        aria-expanded={isOpen}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-90" : "rotate-0"
          }`}
        >
          <path
            d="M3 6h18M3 12h18M3 18h18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          <nav className="py-1">
            <NextLink
              href="/experience"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              List your Experience
            </NextLink>
            <NextLink
              href="/partner/transport"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 17h8m-8 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m12 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m4 0h1a1 1 0 001-1v-3.28a1 1 0 00-.22-.624l-2.4-3a1 1 0 00-.78-.376H15M4 17V8a1 1 0 011-1h9a1 1 0 011 1v9M4 17H2m2-4h11"
                />
              </svg>
              List Your Transport
            </NextLink>
            <NextLink
              href="/tour-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Become a Guide
            </NextLink>
            <NextLink
              href="/footer/help/support-center"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Support Center
            </NextLink>
            {/* <NextLink
              href="/contact"
              className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-1a4 4 0 00-5-3.87M9 20H4v-1a4 4 0 015-3.87m8-5.13a4 4 0 11-8 0 4 4 0 018 0zM5 7a4 4 0 118 0 4 4 0 01-8 0z"
                />
              </svg>
              Our Team
            </NextLink> */}
          </nav>
        </div>
      )}
    </div>
  );
}

// Explore Dropdown Component (NEW)

function ExploreDropdown() {
  const pathname = usePathname();
  const isActive = pathname.startsWith("/activities");
  const isHomePage = pathname === "/";

  return (
    <NextLink
      href="/activities"
      className={`relative flex items-center gap-1.5 transition-colors duration-200 ${
        isActive
          ? isHomePage
            ? "text-white font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-white"
            : "text-purple-600 font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
          : isHomePage
            ? "text-white/90 hover:text-white"
            : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400"
      }`}
    >
      <svg
        className="w-4 h-4 transition-transform duration-300 hover:rotate-12 hover:scale-125"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
      Experience
    </NextLink>
  );
}

// function ExploreDropdown() {
//   const [isOpen, setIsOpen] = useState(false);
//   const dropdownRef = useRef<HTMLDivElement>(null);
//   const pathname = usePathname();

//   useEffect(() => {
//     function handleClickOutside(event: MouseEvent) {
//       if (
//         dropdownRef.current &&
//         !dropdownRef.current.contains(event.target as Node)
//       ) {
//         setIsOpen(false);
//       }
//     }

//     function handleEscapeKey(event: KeyboardEvent) {
//       if (event.key === "Escape") {
//         setIsOpen(false);
//       }
//     }

//     document.addEventListener("mousedown", handleClickOutside);
//     document.addEventListener("keydown", handleEscapeKey);

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//       document.removeEventListener("keydown", handleEscapeKey);
//     };
//   }, []);

//   const isActive = pathname.startsWith("/activities");

//   return (
//     <div className="relative" ref={dropdownRef}>
//       <button
//         onClick={() => setIsOpen(!isOpen)}
//         onMouseEnter={() => setIsOpen(true)}
//         className={`relative flex items-center gap-1 transition-colors duration-200 ${
//           isActive
//             ? "text-purple-600 font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
//             : "text-gray-700 hover:text-purple-600"
//         }`}
//         aria-label="Explore menu"
//         aria-expanded={isOpen}
//       >
//         Explore
//         <svg
//           width="16"
//           height="16"
//           viewBox="0 0 24 24"
//           fill="none"
//           className={`transition-transform duration-200 ${
//             isOpen ? "rotate-180" : ""
//           }`}
//         >
//           <path
//             d="M6 9l6 6 6-6"
//             stroke="currentColor"
//             strokeWidth="2"
//             strokeLinecap="round"
//           />
//         </svg>
//       </button>

//       {isOpen && (
//         <div
//           className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
//           onMouseLeave={() => setIsOpen(false)}
//         >
//           <nav className="py-1">
//             <NextLink
//               href="/activities/adventures-and-outdoors"
//               className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
//               onClick={() => setIsOpen(false)}
//             >
//               Adventures and Outdoors
//             </NextLink>
//             <NextLink
//               href="/activities/wellness-and-relaxation"
//               className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
//               onClick={() => setIsOpen(false)}
//             >
//               Wellness and Relaxation
//             </NextLink>
//             <NextLink
//               href="/activities/culinary-and-local-life"
//               className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
//               onClick={() => setIsOpen(false)}
//             >
//               Culinary and Local Life
//             </NextLink>
//             <NextLink
//               href="/activities/culture-and-heritage"
//               className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
//               onClick={() => setIsOpen(false)}
//             >
//               Culture and Heritage
//             </NextLink>
//             <NextLink
//               href="/activities/seasonal-events-and-festivals"
//               className="flex items-center px-4 py-3 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
//               onClick={() => setIsOpen(false)}
//             >
//               Seasonal Events and Festivals
//             </NextLink>
//           </nav>
//         </div>
//       )}
//     </div>
//   );
// }

// Main navigation tabs component (UPDATED)
const navIconStyle = `
  @keyframes wiggle {
    0%, 100% { transform: rotate(-12deg); }
    50% { transform: rotate(12deg); }
  }
  @keyframes doorOpen {
    0% { transform: scaleX(1); }
    50% { transform: scaleX(0.7) translateX(-2px); }
    100% { transform: scaleX(1); }
  }
  @keyframes pinDrop {
    0% { transform: translateY(-6px) scale(1.2); opacity: 0; }
    60% { transform: translateY(2px) scale(0.9); opacity: 1; }
    80% { transform: translateY(-2px) scale(1.05); }
    100% { transform: translateY(0) scale(1); opacity: 1; }
  }
  @keyframes boltZap {
    0% { transform: rotate(0deg) scale(1); }
    25% { transform: rotate(-20deg) scale(1.3); }
    50% { transform: rotate(15deg) scale(0.9); }
    75% { transform: rotate(-10deg) scale(1.15); }
    100% { transform: rotate(0deg) scale(1); }
  }
  @keyframes walk {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    25% { transform: translateX(-2px) rotate(-5deg); }
    75% { transform: translateX(2px) rotate(5deg); }
  }
  .icon-hotel:hover { animation: wiggle 0.5s ease-in-out; }
  .icon-pin:hover { animation: pinDrop 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97); }
  .icon-bolt:hover { animation: boltZap 0.5s ease-in-out; }
  .icon-guide:hover { animation: walk 0.6s ease-in-out; }
  @keyframes driveBy {
    0% { transform: translateX(0); }
    25% { transform: translateX(-2px); }
    75% { transform: translateX(2px); }
    100% { transform: translateX(0); }
  }
   .icon-transport:hover { animation: driveBy 0.5s ease-in-out; }
  @keyframes takeOff {
    0% { transform: translate(0, 0) rotate(0deg); }
    50% { transform: translate(3px, -3px) rotate(15deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }
  .icon-flight:hover { animation: takeOff 0.5s ease-in-out; }
`;
function ToursDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const handleMouseEnter = () => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setIsOpen(false), 120);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const isActive =
    pathname.startsWith("/tour-guides") ||
    pathname.startsWith("/tour-guides/tour-packages");
  const isHomePage = pathname === "/";

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setIsOpen((o) => !o)}
        className={`relative flex items-center gap-1.5 transition-colors duration-200 ${
          isActive
            ? isHomePage
              ? "text-white font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-white"
              : "text-purple-600 font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
            : isHomePage
              ? "text-white/90 hover:text-white"
              : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400"
        }`}
      >
        <svg
          className="icon-guide w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Tours
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Invisible bridge to prevent gap-triggered close */}
      {isOpen && <div className="absolute left-0 top-full h-2 w-full" />}

      {isOpen && (
        <div className="absolute left-0 top-[calc(100%+8px)] w-56 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden z-50">
          {/* Top accent */}

          <div className="p-1.5">
            <NextLink
              href="/tour-guides"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300 transition-colors group"
            >
              <span className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50 transition-colors">
                <svg
                  className="w-4 h-4 text-purple-600 dark:text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </span>
              <div>
                <p className="font-semibold">Tour Guides</p>
              </div>
            </NextLink>

            <div className="my-1 border-t border border-gray-600 dark:border-white mx-3" />

            <NextLink
              href="/tour-guides/tour-packages"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300 transition-colors group"
            >
              <span className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50 transition-colors">
                <svg
                  className="w-4 h-4 text-purple-600 dark:text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </span>
              <div>
                <p className="font-semibold">Tour Packages</p>
              </div>
            </NextLink>
          </div>
        </div>
      )}
    </div>
  );
}

function MainNav() {
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  const isActive = (path: string) => {
    if (path === "/") {
      return pathname === "/";
    }
    return pathname.startsWith(path);
  };

  const linkClass = (path: string) => {
    const base = "relative transition-colors duration-200";
    const active = isHomePage
      ? "text-white font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-white"
      : "text-purple-600 font-semibold after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-0.5 after:bg-purple-600";
    const inactive = isHomePage
      ? "text-white/90 hover:text-white"
      : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400";
    return `${base} ${isActive(path) ? active : inactive}`;
  };

  return (
    <>
      <style>{navIconStyle}</style>
      <nav className="flex items-center space-x-6">
        <NextLink
          href="/"
          className={`${linkClass("/")} flex items-center gap-1.5 whitespace-nowrap`}
        >
          <svg
            className="w-4 h-4 cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1V9.5z"
            />
          </svg>
          Home
        </NextLink>
        <NextLink
          href="/hotels"
          className={`${linkClass("/hotels")} flex items-center gap-1.5 whitespace-nowrap`}
        >
          <svg
            className="icon-hotel w-4 h-4 cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 21h18M5 21V7a2 2 0 012-2h6a2 2 0 012 2v14M15 21v-7a2 2 0 012-2h2a2 2 0 012 2v7"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 9h.01M9 13h.01M9 17h.01"
            />
          </svg>
          Hotels & Homes
        </NextLink>
        <ExploreDropdown />
        <NextLink
          href="/travel-guides"
          className={`${linkClass("/travel-guides")} flex items-center gap-1.5 whitespace-nowrap`}
        >
          <svg
            className="icon-pin w-4 h-4 cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="whitespace-nowrap">Attractions</span>
        </NextLink>
        <NextLink
          href="/transport"
          className={`${linkClass("/transport")} flex items-center gap-1.5 whitespace-nowrap`}
        >
          <svg
            className="icon-transport w-4 h-4 cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 17h8m-8 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m12 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m4 0h1a1 1 0 001-1v-3.28a1 1 0 00-.22-.624l-2.4-3a1 1 0 00-.78-.376H15M4 17V8a1 1 0 011-1h9a1 1 0 011 1v9M4 17H2m2-4h11"
            />
          </svg>
          Transport
        </NextLink>
        <NextLink
          href="/flights"
          className={`${linkClass("/flights")} flex items-center gap-1.5 whitespace-nowrap`}
        >
          <svg
            className="icon-flight w-4 h-4 cursor-pointer"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2.5 1.5V22l4-1 4 1v-1.5L13 19v-5.5l8 2.5z" />
          </svg>
          Flights
        </NextLink>
        <ToursDropdown />
      </nav>
    </>
  );
}
function MobileToursAccordion({
  isActiveMobile,
  onClose,
}: {
  isActiveMobile: (path: string) => boolean;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-3 text-base font-medium text-gray-900 dark:text-white"
      >
        <span className="flex items-center gap-3">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Tours
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="mb-2 space-y-1">
          <NextLink
            href="/tour-guides"
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              isActiveMobile("/tour-guides") &&
              !isActiveMobile("/tour-guides/tour-packages")
                ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-semibold"
                : "text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700"
            }`}
          >
            <span className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </span>
            <div>
              <p className="font-semibold">Tour Guides</p>
            </div>
          </NextLink>

          <NextLink
            href="/tour-guides/tour-packages"
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              isActiveMobile("/tour-guides/tour-packages")
                ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-semibold"
                : "text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700"
            }`}
          >
            <span className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </span>
            <div>
              <p className="font-semibold">Tour Packages</p>
            </div>
          </NextLink>
        </div>
      )}
    </div>
  );
}

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const isHomePage = pathname === "/";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target as Node)
      ) {
        setMobileOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [mobileOpen]);

  const isActiveMobile = (path: string) => {
    if (path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };

  const mobileLinkClass = (path: string) => {
    const base =
      "flex items-center py-3 px-4 rounded-lg font-medium transition-colors";
    const active = "bg-purple-50 text-purple-600 border-l-4 border-purple-600";
    const inactive = "text-gray-700 hover:bg-gray-50";
    return `${base} ${isActiveMobile(path) ? active : inactive}`;
  };

  return (
    <>
      <header
        data-transparent={isHomePage}
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
          isHomePage
            ? "bg-gradient-to-b from-black/50 via-black/20 to-transparent border-transparent"
            : "bg-white/90 dark:bg-black/90 backdrop-blur-sm border-b border-transparent dark:border-gray-800"
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo */}
            <NextLink
              href="/"
              className="inline-flex items-center gap-3 mr-6 lg:mr-10"
            >
              <Image
                src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/logo/logo_white1.png`}
                alt="LilyLanka logo"
                width={60}
                height={60}
                priority
                sizes="60px"
                unoptimized
              />
            </NextLink>

            {/* Desktop Nav */}
            <div className="hidden lg:flex flex-1 items-center justify-center">
              <MainNav />
            </div>

            {/* Desktop Controls */}
            <div className="hidden lg:flex flex-1 justify-end items-center gap-3">
              <ThemeToggle />
              {/* <CurrencyDropdown />  this is the USD LKR part*/}
              {!isAuthenticated && (
                <>
                  <NextLink
                    href="/partner"
                    target="_blank"
                    rel="noopener noreferrer"
                    // className="text-purple-700 hover:text-gray-600 font-bold text-md whitespace-nowrap px-4 py-2 transition-colors"
                    className={`font-bold text-md whitespace-nowrap px-4 py-2 transition-colors ${
                      isHomePage
                        ? "text-white hover:text-purple-600"
                        : "text-purple-700 hover:text-gray-600"
                    }`}
                  >
                    List Your Property
                  </NextLink>
                  {/* <span className="text-purple-400 text-lg font-thin">|</span> */}
                  <span
                    className={`text-lg font-thin ${isHomePage ? "text-white/40" : "text-purple-400"}`}
                  >
                    |
                  </span>
                </>
              )}
              {/* {!isAuthenticated && (
                <>
                  <NextLink
                    href="/customer/sign-in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-700 hover:text-gray-600 font-bold text-md whitespace-nowrap px-4 py-2 transition-colors"
                  >
                    Sign In
                  </NextLink>
                </>
              )} */}
              {!isAuthenticated && <SignInDropdown />}
              <ProfileDropdown />
              <DesktopHamburgerDropdown />
            </div>

            {/* Mobile Controls */}
            <div className="flex lg:hidden items-center gap-3">
              <ProfileDropdown />
              <button
                ref={mobileButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileOpen((prev) => !prev);
                }}
                className={`p-2 bg-transparent transition-colors ${
                  isHomePage
                    ? "text-white hover:opacity-80"
                    : "text-gray-700 dark:text-gray-200 hover:text-purple-600 dark:hover:text-purple-400"
                }`}
                aria-label="Toggle menu"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  {mobileOpen ? (
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  ) : (
                    <path
                      d="M3 6h18M3 12h18M3 18h18"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile overlay OUTSIDE header */}
      <div
        className={`lg:hidden fixed inset-0 bg-white/80 dark:bg-gray-950/95 backdrop-blur-xl z-[100] overflow-y-auto transition-opacity duration-300 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Close button */}
        <button
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="absolute top-5 right-5 p-2 text-gray-600 hover:text-purple-600 transition-colors"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="px-6 pt-16 pb-10">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Explore
            </h3>
            <ThemeToggle />
          </div>
          <div className="flex flex-col">
            <NextLink
              href="/"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/") && pathname === "/" ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1V9.5z"
                />
              </svg>
              Home
            </NextLink>
            <NextLink
              href="/hotels"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/hotels") ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5 transition-transform duration-300 group-hover:animate-bounce"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 21h18M5 21V7a2 2 0 012-2h6a2 2 0 012 2v14M15 21v-7a2 2 0 012-2h2a2 2 0 012 2v7"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 9h.01M9 13h.01M9 17h.01"
                />
              </svg>
              Hotels & Homes
            </NextLink>
            <NextLink
              href="/activities"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/activities") ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Experience
            </NextLink>
            <NextLink
              href="/travel-guides"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/travel-guides") ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Attractions
            </NextLink>
            <NextLink
              href="/transport"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/transport") ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 17h8m-8 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m12 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m4 0h1a1 1 0 001-1v-3.28a1 1 0 00-.22-.624l-2.4-3a1 1 0 00-.78-.376H15M4 17V8a1 1 0 011-1h9a1 1 0 011 1v9M4 17H2m2-4h11"
                />
              </svg>
              Transport
            </NextLink>
            <NextLink
              href="/flights"
              className={`py-3 text-base border-b border-gray-100 transition-colors flex items-center gap-3 ${isActiveMobile("/flights") ? "text-purple-600 font-medium" : "text-gray-700 hover:text-purple-600"}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2.5 1.5V22l4-1 4 1v-1.5L13 19v-5.5l8 2.5z" />
              </svg>
              Flights
            </NextLink>
            <MobileToursAccordion
              isActiveMobile={isActiveMobile}
              onClose={() => setMobileOpen(false)}
            />
          </div>

          {/* Account */}
          {!isAuthenticated && (
            <>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-4">
                Account
              </h3>
              <div className="flex flex-col">
                {/* <NextLink
                  href="/customer/sign-in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign In
                </NextLink> */}
                {/* Customer Sign In */}
                <NextLink
                  href="/customer/sign-in"
                  className="py-3 text-base text-gray-700 dark:text-gray-200 hover:text-purple-600 border-b border-gray-100 dark:border-gray-800 transition-colors flex items-center gap-3"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-purple-600 dark:text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-sm">Sign in as Customer</p>
                    <p className="text-xs text-gray-400">
                      Book hotels, tours & experiences
                    </p>
                  </div>
                </NextLink>
                {/* Partner Sign In */}
                <NextLink
                  href="/partner/sign-in-property"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-3 text-base text-gray-700 dark:text-gray-200 hover:text-purple-600 border-b border-gray-100 dark:border-gray-800 transition-colors flex items-center gap-3"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-amber-600 dark:text-amber-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-sm">Sign in as Partner</p>
                    <p className="text-xs text-gray-400">
                      Manage your property & listings
                    </p>
                  </div>
                </NextLink>
                <NextLink
                  href="/customer/sign-up"
                  className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                  Create Account
                </NextLink>
              </div>
            </>
          )}

          {/* Partner */}
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-4">
            Partner
          </h3>
          <div className="flex flex-col">
            <NextLink
              href="/partner"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              List Your Property
            </NextLink>
            <NextLink
              href="/experience"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              List your Experience
            </NextLink>
            <NextLink
              href="/partner/transport"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 17h8m-8 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m12 0a2 2 0 11-4 0m4 0a2 2 0 10-4 0m4 0h1a1 1 0 001-1v-3.28a1 1 0 00-.22-.624l-2.4-3a1 1 0 00-.78-.376H15M4 17V8a1 1 0 011-1h9a1 1 0 011 1v9M4 17H2m2-4h11"
                />
              </svg>
              List Your Transport
            </NextLink>
            <NextLink
              href="/tour-guide"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Become a Tour Guide
            </NextLink>
          </div>

          {/* Help & Support */}
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-4">
            Help & Support
          </h3>
          <div className="flex flex-col">
            <NextLink
              href="/footer/help/support-center"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Support Center
            </NextLink>
            {/* <NextLink
              href="/contact"
              className="py-3 text-base text-gray-700 hover:text-purple-600 border-b border-gray-100 transition-colors flex items-center gap-3"
              onClick={() => setMobileOpen(false)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-1a4 4 0 00-5-3.87M9 20H4v-1a4 4 0 015-3.87m8-5.13a4 4 0 11-8 0 4 4 0 018 0zM5 7a4 4 0 118 0 4 4 0 01-8 0z"
                />
              </svg>
              Our Team
            </NextLink> */}
          </div>
        </div>
      </div>
    </>
  );
}
